/**
 * Validates currency format for correct comma and period placement.
 * 
 * Rules:
 * - Commas must separate groups of exactly 3 digits (e.g., $50,000 valid, $500,00 invalid)
 * - Only one period is allowed (for decimal portion)
 * - Optional leading $ sign and negative sign
 * 
 * @param value The currency string to validate
 * @returns {{ isValid: boolean, message: string }}
 */
const validateCurrencyFormat = (value: string): { isValid: boolean; message: string } => {
  if (!value || value.trim().length === 0) {
    return { isValid: true, message: '' };
  }

  // Strip leading $ and/or negative sign
  const stripped = value.replace(/^[($\-\s]+|[)\s]+$/g, '').replace(/^\$/, '');

  // Check for more than one period
  if ((stripped.match(/\./g) || []).length > 1) {
    return { isValid: false, message: 'Invalid currency format: multiple decimal points detected.' };
  }

  // If no commas, no further comma-validation needed
  if (!stripped.includes(',')) {
    return { isValid: true, message: '' };
  }

  // Validate comma placement: must match pattern like 1,000 or 12,000,000 etc.
  // Split on period first to only validate the integer portion
  const [integerPart] = stripped.split('.');

  // Regex: optional 1-3 leading digits, then groups of exactly 3 digits preceded by comma
  const commaPattern = /^\d{1,3}(,\d{3})*$/;
  if (!commaPattern.test(integerPart)) {
    return { isValid: false, message: 'Invalid currency format: commas are not in the correct position.' };
  }

  return { isValid: true, message: '' };
};

export default validateCurrencyFormat;
